/**
 * @file IndividualReport.js
 * @description CSV report export configuration and trigger for individual demographic report data.
 */

import { formatPhoneNumber, formatDate } from '../../../utils/utils';
import { getIndividualReportData } from '../../../services/clientService';
import {
    createCsvReportDefinition,
    exportCsvReport,
} from '../../../utils/reporting/csvReportExporter';

const individualReportHeaders = [
    'Client Code', 'Unique ID', 'First Name', 'Middle Name', 'Last Name', 'Birth Date', 'Email Address',
    'Phone Number', 'Address', 'Address2', 'City', 'State', 'Zip', 'SSN', 'Gender', 'Category', 'Status', 'Hire Date',
    'Term Date', 'Full Time', 'Title', 'Pay Frequency', 'Location', 'Location Name', 'Pay Group', 'Pay Group Name', 'Work State',
    'Department', 'Department Name', 'Division', 'Division Name', 'Region', 'Region Name', 'Business Unit Name', 'Business Unit',
    'Salary', 'Hourly Rate', 'Test', 'Custom Text 1', 'Custom Text 2', 'Custom Text 3',
    'Custom Text 4', 'Custom Text 5', 'Custom Text 6', 'Custom Text 7', 'Custom Text 8', 'Custom Text 9', 'Custom Text 10',
    'Custom Date 1', 'Custom Date 2', 'Custom Date 3', 'Custom Date 4', 'Custom Date 5', 'Custom Money 1', 'Custom Money 2',
    'Custom Money 3', 'Custom Money 4', 'Custom Money 5, Account Status',
];

function mapIndividualRow(demographic) {
    return {
        clientCode: demographic.clientCode,
        sourceId: demographic.sourceId,
        firstName: demographic.firstName,
        middleName: demographic.middleName,
        lastName: demographic.lastName,
        birthday: demographic.birthday ? formatDate(demographic.birthday) : '',
        emailAddress: demographic.emailAddress,
        mobilePhoneNumber: demographic.mobilePhoneNumber ? formatPhoneNumber(demographic.mobilePhoneNumber) : '',
        address: demographic.address,
        address2: demographic.address2,
        city: demographic.city,
        stateAbbreviation: demographic.stateAbbreviation,
        zip: demographic.zip,
        ssn: demographic.ssn,
        genderCode: demographic.genderCode,
        employmentCategoryCode: demographic.employmentCategoryCode,
        employmentStatusCode: demographic.employmentStatusCode,
        hireDate: demographic.hireDate ? formatDate(demographic.hireDate) : '',
        termDate: demographic.termDate ? formatDate(demographic.termDate) : '',
        isFullTimeEmployee: demographic.isFullTimeEmployee,
        jobTitle: demographic.jobTitle,
        payFrequencyCode: demographic.payFrequencyCode,
        locationCode: demographic.locationCode,
        locationName: demographic.locationName,
        payGroupCode: demographic.payGroupCode,
        payGroupName: demographic.payGroupName,
        workState: demographic.workState,
        departmentCode: demographic.departmentCode,
        departmentName: demographic.departmentName,
        divisionCode: demographic.divisionCode,
        divisionName: demographic.divisionName,
        regionCode: demographic.regionCode,
        regionName: demographic.regionName,
        businessUnitCode: demographic.businessUnitCode,
        businessUnitName: demographic.businessUnitName,
        salary: demographic.salary,
        hourlyRate: demographic.hourlyRate,
        isTestRecord: demographic.isTestRecord,
        customText1: demographic.customText1,
        customText2: demographic.customText2,
        customText3: demographic.customText3,
        customText4: demographic.customText4,
        customText5: demographic.customText5,
        customText6: demographic.customText6,
        customText7: demographic.customText7,
        customText8: demographic.customText8,
        customText9: demographic.customText9,
        customText10: demographic.customText10,
        customDate1: demographic.customDate1 ? formatDate(demographic.customDate1) : '',
        customDate2: demographic.customDate2 ? formatDate(demographic.customDate2) : '',
        customDate3: demographic.customDate3 ? formatDate(demographic.customDate3) : '',
        customDate4: demographic.customDate4 ? formatDate(demographic.customDate4) : '',
        customDate5: demographic.customDate5 ? formatDate(demographic.customDate5) : '',
        customMoney1: demographic.customMoney1,
        customMoney2: demographic.customMoney2,
        customMoney3: demographic.customMoney3,
        customMoney4: demographic.customMoney4,
        customMoney5: demographic.customMoney5,
        accountStatus: demographic.tenantUserStatus,
    };
}

async function getIndividualData() {
    return getIndividualReportData();
}

function sortIndividuals(firstIndividual, secondIndividual) {
    return firstIndividual.lastName.localeCompare(secondIndividual.lastName)
        || firstIndividual.firstName.localeCompare(secondIndividual.firstName);
}

const individualReportDefinition = createCsvReportDefinition({
    filenamePrefix: 'individualReport',
    getData: getIndividualData,
    mapRow: mapIndividualRow,
    sortRows: sortIndividuals,
});

export async function downloadReport() {
    await exportCsvReport(individualReportDefinition);
}
